import React, { useState, useEffect, useRef } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Dialog,
  DialogContent,
  Button,
  TextField,
  Box,
  CircularProgress,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  Typography,
  Divider,
  Chip,
  InputAdornment,
  Tooltip,
  useTheme,
  alpha,
  Card,
  CardContent,
  Stack,
  Stepper,
  Step,
  StepLabel,
  LinearProgress,
  Container,
  Alert,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  ShoppingCart as ShoppingCartIcon,
  Inventory as InventoryIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Business as BusinessIcon,
  Warehouse as WarehouseIcon,
  CalendarToday as CalendarIcon,
  LocalShipping as ShippingIcon,
  CheckCircle as CheckCircleIcon,
  Category as CategoryIcon,
  Scale as ScaleIcon,
  Store as StoreIcon,
  AccountBalance as AccountIcon,
  PersonOutline as PersonIcon,
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'react-toastify';
import {
  PurchaseOrder,
  CreatePurchaseOrderDto,
  UpdatePurchaseOrderDto,
} from '../../types/purchaseOrders.types';
import { purchaseOrdersService } from '../../services/purchaseOrders.service';
import suppliersService from '../../services/suppliers.service';
import { warehousesService } from '../../services/warehouses.service';
import { branchesService } from '../../services/branches.service';
import { itemsService, itemUnitsService, unitsService } from '../../services/items.service';
import { itemSuppliersService } from '../../services/itemSuppliers.service';
import { custodiesService } from '../../services/custodies.service';
import axiosInstance from '../../services/axiosInstance';
import { chartOfAccountsService } from '../../services/chartOfAccounts.service';
import { useFiscalDateRange } from '../../hooks/useFiscalDateRange';
import { useGridEnterNavigation } from '../../hooks/useGridEnterNavigation';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { Supplier } from '../../types/suppliers.types';
import { Warehouse } from '../../types/warehouses.types';
import { Branch } from '../../types/branches.types';
import { Item } from '../../types/items.types';
import { ItemBySupplier, ItemPolicy } from '../../types/itemSuppliers.types';
import { Custody } from '../../types/custodies.types';
import { ChartOfAccount } from '../../types/chartOfAccount';

const translations = {
  en: {
    title: 'Purchase Order',
    orderDetails: 'Order Details',
    items: 'Items',
    summary: 'Summary',
    poNumber: 'PO #',
    poDate: 'Order Date',
    paymentSource: 'Payment Source',
    supplier: 'Supplier',
    custody: 'Employee Custody',
    paymentAccount: 'Payment Account',
    warehouse: 'Warehouse',
    branch: 'Branch',
    delivery: 'Expected Delivery',
    item: 'Item',
    unit: 'Unit/Pkg',
    quantity: 'Qty',
    price: 'Price',
    discount: 'Disc %',
    tax: 'Tax %',
    total: 'Total',
    addItem: 'Add Item',
    headerDiscount: 'Order Discount %',
    headerTax: 'Order Tax %',
    shipping: 'Shipping',
    subtotal: 'Subtotal',
    grandTotal: 'Grand Total',
    save: 'Save Order',
    cancel: 'Cancel',
    notes: 'Notes',
    selectSupplier: 'Please select supplier first',
    selectItem: 'Select item...',
    selectUnit: 'Select unit...',
    selectBranch: 'Select branch (optional)',
    selectCustody: 'Select custody...',
    selectAccount: 'Select account...',
    itemsAvailable: 'items available',
    priceFromSupplier: 'Price locked by supplier',
    itemNotAvailable: 'This item is not available from selected supplier',
    supplierRequired: 'This item requires a supplier to be selected first',
    orderSaved: 'Order saved successfully!',
    orderSaveFailed: 'Failed to save order',
    paymentSourceRequired: 'Please select payment source',
    onePaymentSourceOnly: 'Please select only ONE payment source',
    // Item Policy Labels
    policySupplierOnly: 'Supplier Required',
    policyPriceOnly: 'Price Locked',
    policySupplierAndPrice: 'Supplier + Price Locked',
  },
  ar: {
    title: 'أمر شراء',
    orderDetails: 'بيانات الأمر',
    items: 'الأصناف',
    summary: 'الملخص',
    poNumber: 'رقم الأمر',
    poDate: 'تاريخ الأمر',
    paymentSource: 'مصدر الدفع',
    supplier: 'المورد',
    custody: 'عهدة موظف',
    paymentAccount: 'حساب الدفع',
    warehouse: 'المخزن',
    branch: 'الفرع',
    delivery: 'التسليم المتوقع',
    item: 'الصنف',
    unit: 'الوحدة/التعبئة',
    quantity: 'الكمية',
    price: 'السعر',
    discount: 'خصم %',
    tax: 'ضريبة %',
    total: 'الإجمالي',
    addItem: 'إضافة صنف',
    headerDiscount: 'خصم الأمر %',
    headerTax: 'ضريبة الأمر %',
    shipping: 'الشحن',
    subtotal: 'المجموع الفرعي',
    grandTotal: 'الإجمالي الكلي',
    save: 'حفظ الأمر',
    cancel: 'إلغاء',
    notes: 'ملاحظات',
    selectSupplier: 'اختر المورد أولاً',
    selectItem: 'اختر الصنف...',
    selectUnit: 'اختر الوحدة...',
    selectBranch: 'اختر الفرع (اختياري)',
    selectCustody: 'اختر العهدة...',
    selectAccount: 'اختر الحساب...',
    itemsAvailable: 'صنف متوفر',
    priceFromSupplier: 'سعر محدد من المورد',
    itemNotAvailable: 'هذا الصنف غير متوفر من المورد المختار',
    supplierRequired: 'هذا الصنف يتطلب اختيار المورد أولاً',
    orderSaved: 'تم حفظ الأمر بنجاح!',
    orderSaveFailed: 'فشل حفظ الأمر',
    paymentSourceRequired: 'يجب اختيار مصدر الدفع',
    onePaymentSourceOnly: 'يجب اختيار مصدر دفع واحد فقط',
    // Item Policy Labels
    policySupplierOnly: 'مورد إجباري',
    policyPriceOnly: 'سعر مقفول',
    policySupplierAndPrice: 'مورد + سعر إجباري',
  },
};

interface PurchaseOrderDialogProps {
  open: boolean;
  onClose: () => void;
  purchaseOrder?: PurchaseOrder;
  onSave: () => void;
}

interface Unit {
  unitId: number;
  unitName: string;
  unitNameAr: string;
  unitCode: string;
}

interface ItemUnit {
  unitId: number;
  unitName: string;
  unitNameAr: string;
  unitCode: string;
  conversionFactor: number;
  defaultPurchasePrice: number;
  defaultSalePrice: number;
  isDefaultPurchase: boolean;
  isDefaultSale: boolean;
}

interface DetailLine {
  id: string;
  itemId: number | null;
  item: Item | null;
  unitId: number | null;
  unit: Unit | null;
  availableUnits: ItemUnit[];
  orderedQuantity: number;
  unitPrice: number;
  discountPercent: number;
  taxPercent: number;
  notes: string;
  isPriceReadOnly?: boolean;
  itemPolicy?: ItemPolicy;
}

const PurchaseOrderDialog: React.FC<PurchaseOrderDialogProps> = ({
  open,
  onClose,
  purchaseOrder,
  onSave,
}) => {
  const { language } = useLanguage();
  const theme = useTheme();
  const t = translations[language];
  const isRTL = language === 'ar';
  
  const { minDate, maxDate } = useFiscalDateRange();
  const isSuperAdmin = useSelector((state: RootState) => state.auth.user?.isSuperAdmin) ?? false;
  const isEditMode = !!purchaseOrder;
  const dateLocked = isEditMode && !isSuperAdmin;

  const gridRef = useRef<HTMLDivElement>(null);
  useGridEnterNavigation(gridRef);

  const getPolicyLabel = (policy: ItemPolicy | undefined): string => {
    if (!policy) return '';
    const isSupplierReq = policy.isSupplierRequired;
    const isPriceCtrl = policy.isPriceControlled;
    if (isSupplierReq && isPriceCtrl) return t.policySupplierAndPrice;
    else if (isSupplierReq) return t.policySupplierOnly;
    else if (isPriceCtrl) return t.policyPriceOnly;
    return '';
  };

  const [activeStep, setActiveStep] = useState(0);
  const steps = [t.orderDetails, t.items, t.summary];

  // Payment Source: 'supplier' | 'custody' | 'account'
  const [paymentSource, setPaymentSource] = useState<'supplier' | 'custody' | 'account'>('supplier');
  const [isBranchManager, setIsBranchManager] = useState(false);
  const [isWarehouseKeeper, setIsWarehouseKeeper] = useState(false);

  const [formData, setFormData] = useState({
    poNumber: '',
    poDate: new Date().toISOString().split('T')[0],
    supplierId: null as number | null,
    custodyId: null as number | null,
    paymentAccountId: null as number | null,
    warehouseId: null as number | null,
    branchId: null as number | null,
    expectedDeliveryDate: '',
    discountPercent: 0,
    taxPercent: 0,
    shippingCost: 0,
    notes: '',
  });

  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [custodies, setCustodies] = useState<Custody[]>([]);
  const [paymentAccounts, setPaymentAccounts] = useState<any[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [supplierItems, setSupplierItems] = useState<ItemBySupplier[]>([]);

  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [selectedCustody, setSelectedCustody] = useState<Custody | null>(null);
  const [selectedPaymentAccount, setSelectedPaymentAccount] = useState<any | null>(null);
  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse | null>(null);
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);

  const [detailLines, setDetailLines] = useState<DetailLine[]>([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      loadMasterData();
      setActiveStep(0);
    }
  }, [open]);

  useEffect(() => {
    if (open && suppliers.length > 0 && warehouses.length > 0 && items.length > 0) {
      if (purchaseOrder) {
        loadPurchaseOrder();
      } else {
        loadNewPONumber();
        addNewLine();
      }
    }
  }, [open, purchaseOrder, suppliers, warehouses, items]);

  useEffect(() => {
    if (formData.supplierId) {
      loadSupplierItems(formData.supplierId);
      updatePricesForSupplier(formData.supplierId);
    } else {
      setSupplierItems([]);
    }
  }, [formData.supplierId]);

  const loadMasterData = async () => {
    try {
      const [suppliersData, custodiesData, accountsData, warehousesData, branchesData, itemsData] = await Promise.all([
        suppliersService.getAll(),
        axiosInstance.get('/Custodies/purchase-lookup').then(res => res.data),
        chartOfAccountsService.getAll(),
        warehousesService.getOperational(),
        branchesService.getAll(),
        itemsService.getAllExcludingBakeryPurchase(),
      ]);
      setSuppliers(suppliersData);

      // Read user branches from JWT token
      const token = localStorage.getItem('token');
      let userBranchIds: number[] = [];
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const primaryBranch = payload.BranchId ? [Number(payload.BranchId)] : [];
          const allowedBranches = payload.AllowedBranchId
            ? (Array.isArray(payload.AllowedBranchId) ? payload.AllowedBranchId.map(Number) : [Number(payload.AllowedBranchId)])
            : [];
          userBranchIds = Array.from(new Set([...primaryBranch, ...allowedBranches]));
        } catch (e) { console.error('Failed to parse token:', e); }
      }
      setIsBranchManager(userBranchIds.length > 0);

      // Read keeper warehouse IDs from JWT token
      let userWarehouseIds: number[] = [];
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const keeperClaims = payload.KeeperWarehouseId;
          userWarehouseIds = Array.isArray(keeperClaims) ? keeperClaims.map(Number) : keeperClaims ? [Number(keeperClaims)] : [];
        } catch (e) { console.error('Failed to parse keeper warehouses:', e); }
      }
      setIsWarehouseKeeper(userWarehouseIds.length > 0);

      const custList = Array.isArray(custodiesData) ? custodiesData : (custodiesData?.data || []);
      setCustodies(custList);
    
      setPaymentAccounts(accountsData.filter((a: any) => a.accountType === 'Asset' || a.accountType === 'Liability'));
    
      // Filter warehouses: by keeper (warehouse keeper) or by branch (branch manager) or all
      const filteredWarehouses = userWarehouseIds.length > 0
        ? warehousesData.filter((w: Warehouse) => userWarehouseIds.includes(w.warehouseId))
        : userBranchIds.length === 0
          ? warehousesData
          : warehousesData.filter((w: Warehouse) => 
              w.branchId && userBranchIds.includes(w.branchId)
            );
      setWarehouses(filteredWarehouses);
    
      setBranches(branchesData.filter((b: Branch) => b.branchType === 'Operational'));
      setItems(itemsData);
    } catch (error) {
      console.error('Failed to load master data:', error);
    }
  };

  const loadSupplierItems = async (supplierId: number) => {
    try {
      const items = await itemSuppliersService.getItemsBySupplier(supplierId);
      setSupplierItems(items);
    } catch (error) {
      console.error('Failed to load supplier items:', error);
      setSupplierItems([]);
    }
  };

  const loadPurchaseOrder = () => {
    if (!purchaseOrder) return;

    // Determine payment source
    if (purchaseOrder.supplierId) {
      setPaymentSource('supplier');
    } else if (purchaseOrder.custodyId) {
      setPaymentSource('custody');
    } else if (purchaseOrder.paymentAccountId) {
      setPaymentSource('account');
    }

    setFormData({
      poNumber: purchaseOrder.poNumber,
      poDate: purchaseOrder.poDate.split('T')[0],
      supplierId: purchaseOrder.supplierId || null,
      custodyId: purchaseOrder.custodyId || null,
      paymentAccountId: purchaseOrder.paymentAccountId || null,
      warehouseId: purchaseOrder.warehouseId,
      branchId: purchaseOrder.branchId || null,
      expectedDeliveryDate: purchaseOrder.expectedDeliveryDate?.split('T')[0] || '',
      discountPercent: purchaseOrder.discountPercent || 0,
      taxPercent: purchaseOrder.taxPercent || 0,
      shippingCost: purchaseOrder.shippingCost || 0,
      notes: purchaseOrder.notes || '',
    });

    const supplier = suppliers.find(s => s.supplierId === purchaseOrder.supplierId);
    const custody = custodies.find(c => c.custodyId === purchaseOrder.custodyId);
    const account = paymentAccounts.find(a => a.accountId === purchaseOrder.paymentAccountId);
    const warehouse = warehouses.find(w => w.warehouseId === purchaseOrder.warehouseId);
    const branch = purchaseOrder.branchId ? branches.find(b => b.branchId === purchaseOrder.branchId) : null;
    
    setSelectedSupplier(supplier || null);
    setSelectedCustody(custody || null);
    setSelectedPaymentAccount(account || null);
    setSelectedWarehouse(warehouse || null);
    setSelectedBranch(branch || null);

    const loadLinesAsync = async () => {
      const linesPromises = purchaseOrder.details.map(async (detail) => {
        const item = items.find(i => i.itemId === detail.itemId);
        let itemUnits: any[] = [];
        let selectedUnit = null;
        
        try {
          itemUnits = await itemUnitsService.getItemUnits(detail.itemId);
          selectedUnit = itemUnits.find((u: any) => u.unitId === detail.unitId) || null;
        } catch (error) {
          console.error('Failed to load units:', error);
        }

        return {
          id: `${detail.poDetailId}`,
          itemId: detail.itemId,
          item: item || null,
          unitId: detail.unitId,
          unit: selectedUnit,
          availableUnits: itemUnits,
          orderedQuantity: detail.orderedQuantity,
          unitPrice: detail.unitPrice,
          discountPercent: detail.discountPercent || 0,
          taxPercent: detail.taxPercent || 0,
          notes: detail.notes || '',
        };
      });
      
      const lines = await Promise.all(linesPromises);
      setDetailLines(lines);
    };
    
    loadLinesAsync();
  };

  const loadNewPONumber = async () => {
    try {
      const poNumber = await purchaseOrdersService.generatePONumber();
      setFormData((prev) => ({ ...prev, poNumber }));
    } catch (error) {
      console.error('Failed to generate PO number:', error);
    }
  };

  const addNewLine = () => {
    const newLine: DetailLine = {
      id: Date.now().toString(),
      itemId: null,
      item: null,
      unitId: null,
      unit: null,
      availableUnits: [],
      orderedQuantity: 1,
      unitPrice: 0,
      discountPercent: 0,
      taxPercent: 0,
      notes: '',
    };
    setDetailLines((prev) => [...prev, newLine]);
  };

  const removeLine = (id: string) => {
    setDetailLines((prev) => prev.filter((line) => line.id !== id));
  };

  const updatePricesForSupplier = async (supplierId: number) => {
    if (detailLines.length === 0) return;
    
    const updatedLines = await Promise.all(
      detailLines.map(async (line) => {
        if (!line.itemId || !line.unitId) return line;

        try {
          const priceInfo = await itemSuppliersService.getPrice(
            line.itemId,
            supplierId,
            line.unitId
          );

          if (priceInfo && line.itemPolicy) {
            return {
              ...line,
              unitPrice: priceInfo.price,
              isPriceReadOnly: line.itemPolicy.isPriceControlled,
            };
          }
        } catch (error) {
          // Silently fail
        }
        return line;
      })
    );
    setDetailLines(updatedLines);
  };

  const handleItemChange = async (lineId: string, item: Item | null) => {
    if (!item) {
      updateLine(lineId, 'item', null);
      updateLine(lineId, 'itemId', null);
      updateLine(lineId, 'availableUnits', []);
      updateLine(lineId, 'unit', null);
      updateLine(lineId, 'unitId', null);
      return;
    }

    try {
      const policy = await itemSuppliersService.getItemPolicy(item.itemId);
      
      if (policy.isSupplierRequired && !formData.supplierId) {
        toast.warning(t.supplierRequired);
        return;
      }

      if (formData.supplierId) {
        const approvedSuppliers = await itemSuppliersService.getSuppliersByItem(item.itemId, true);
        
        if (approvedSuppliers.length > 0) {
          const isApproved = approvedSuppliers.some(s => s.supplierId === formData.supplierId);
          
          if (!isApproved) {
            const supplierNames = approvedSuppliers.map(s => 
              language === 'ar' ? s.supplierNameAr : s.supplierName
            ).join(', ');
            
            toast.error(
              language === 'ar' 
                ? `هذا الصنف متاح فقط من الموردين: ${supplierNames}`
                : `This item is only available from suppliers: ${supplierNames}`
            );
            return;
          }
        }
      }

      let units = await itemUnitsService.getItemUnits(item.itemId);
      
      const hasBaseUnit = units.some(u => u.unitId === item.baseUnitId);
      
      if (!hasBaseUnit && item.baseUnitId) {
        const allUnits = await unitsService.getAll();
        const baseUnit = allUnits.find(u => u.unitId === item.baseUnitId);
        
        if (baseUnit) {
          const baseUnitAsItemUnit = {
            itemUnitId: 0,
            itemId: item.itemId,
            unitId: baseUnit.unitId,
            unitName: baseUnit.unitName,
            unitNameAr: baseUnit.unitNameAr,
            unitCode: baseUnit.unitCode,
            conversionFactor: 1,
            defaultSalePrice: 0,
            defaultPurchasePrice: 0,
            isDefaultSale: true,
            isDefaultPurchase: true,
          };
          units = [baseUnitAsItemUnit, ...units];
        }
      }

      let defaultUnit = units.find(u => u.unitId === item.baseUnitId);
      if (!defaultUnit) {
        defaultUnit = units.find(u => u.isDefaultPurchase);
      }
      if (!defaultUnit) {
        defaultUnit = units[0];
      }

      let unitPrice = 0;
      let isPriceReadOnly = false;

      if (formData.supplierId && defaultUnit) {
        const priceInfo = await itemSuppliersService.getPrice(
          item.itemId,
          formData.supplierId,
          defaultUnit.unitId
        );

        if (priceInfo) {
          unitPrice = priceInfo.price;
          isPriceReadOnly = policy.isPriceControlled;
        } else if (defaultUnit) {
          const itemUnit = units.find(u => u.unitId === defaultUnit!.unitId);
          if (itemUnit) {
            unitPrice = itemUnit.defaultPurchasePrice || 0;
          }
        }
      } else if (defaultUnit) {
        // No supplier (custody purchase) — use default purchase price
        const itemUnit = units.find(u => u.unitId === defaultUnit!.unitId);
        if (itemUnit) {
          unitPrice = itemUnit.defaultPurchasePrice || 0;
        }
      }

      setDetailLines((prev) =>
        prev.map((line) =>
          line.id === lineId
            ? {
                ...line,
                item,
                itemId: item.itemId,
                availableUnits: units,
                unit: defaultUnit || null,
                unitId: defaultUnit?.unitId || null,
                unitPrice,
                isPriceReadOnly,
                itemPolicy: policy,
              }
            : line
        )
      );
    } catch (error) {
      console.error('Failed to load item policy:', error);
    }
  };

  const handleUnitChange = async (lineId: string, unit: Unit | null) => {
    if (!unit) {
      updateLine(lineId, 'unit', null);
      updateLine(lineId, 'unitId', null);
      return;
    }

    const line = detailLines.find(l => l.id === lineId);
    if (!line || !line.itemId) {
      updateLine(lineId, 'unit', unit);
      updateLine(lineId, 'unitId', unit.unitId);
      return;
    }

    let finalPrice = 0;

    if (formData.supplierId) {
      try {
        const priceInfo = await itemSuppliersService.getPrice(
          line.itemId,
          formData.supplierId,
          unit.unitId
        );
        if (priceInfo) {
          finalPrice = priceInfo.price;
        }
      } catch (error) {
        // No supplier price
      }
    }

    if (finalPrice === 0 && line.availableUnits) {
      const itemUnit = line.availableUnits.find(u => u.unitId === unit.unitId);
      if (itemUnit) {
        finalPrice = itemUnit.defaultPurchasePrice || 0;
      }
    }

    updateLine(lineId, 'unit', unit);
    updateLine(lineId, 'unitId', unit.unitId);
    updateLine(lineId, 'unitPrice', finalPrice);
  };

  const updateLine = (id: string, field: keyof DetailLine, value: any) => {
    setDetailLines((prev) =>
      prev.map((line) => (line.id === id ? { ...line, [field]: value } : line))
    );
  };

  const calculateLineTotal = (line: DetailLine): number => {
    const lineAmount = line.orderedQuantity * line.unitPrice;
    const discountAmount = lineAmount * (line.discountPercent / 100);
    const amountAfterDiscount = lineAmount - discountAmount;
    const taxAmount = amountAfterDiscount * (line.taxPercent / 100);
    return amountAfterDiscount + taxAmount;
  };

  const calculateTotals = () => {
    const subTotal = detailLines.reduce((sum, line) => {
      const lineAmount = line.orderedQuantity * line.unitPrice;
      const discountAmount = lineAmount * (line.discountPercent / 100);
      const amountAfterDiscount = lineAmount - discountAmount;
      const taxAmount = amountAfterDiscount * (line.taxPercent / 100);
      return sum + (amountAfterDiscount + taxAmount);
    }, 0);

    const headerDiscountAmount = subTotal * (formData.discountPercent / 100);
    const amountAfterHeaderDiscount = subTotal - headerDiscountAmount;
    const headerTaxAmount = amountAfterHeaderDiscount * (formData.taxPercent / 100);
    const totalAmount = amountAfterHeaderDiscount + headerTaxAmount + formData.shippingCost;

    return {
      subTotal,
      discountAmount: headerDiscountAmount,
      taxAmount: headerTaxAmount,
      totalAmount,
    };
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Validate payment source
    const paymentSourceCount = [formData.supplierId, formData.custodyId, formData.paymentAccountId].filter(Boolean).length;
    
    if (paymentSourceCount === 0) {
      newErrors.paymentSource = t.paymentSourceRequired;
    } else if (paymentSourceCount > 1) {
      newErrors.paymentSource = t.onePaymentSourceOnly;
    }
    
    if (!formData.warehouseId) newErrors.warehouseId = 'Required';
    
    if (!isSuperAdmin) {
      if (formData.poDate) {
        const poDate = new Date(formData.poDate);
        poDate.setHours(0, 0, 0, 0);
        if (poDate < today) {
          newErrors.poDate = language === 'ar' ? 'يجب أن يكون التاريخ اليوم أو في المستقبل' : 'Date must be today or later';
        }
      }

      if (formData.expectedDeliveryDate) {
        const deliveryDate = new Date(formData.expectedDeliveryDate);
        deliveryDate.setHours(0, 0, 0, 0);
        if (deliveryDate < today) {
          newErrors.expectedDeliveryDate = language === 'ar' ? 'يجب أن يكون التاريخ اليوم أو في المستقبل' : 'Date must be today or later';
        }
      }
    }
    
    if (detailLines.length === 0) newErrors.details = 'Required';

    detailLines.forEach((line, index) => {
      if (!line.itemId) newErrors[`item_${index}`] = 'Required';
      if (!line.unitId) newErrors[`unit_${index}`] = 'Required';
      if (line.orderedQuantity <= 0) newErrors[`quantity_${index}`] = 'Must be > 0';
    });

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      const messages: string[] = [];
      if (newErrors.paymentSource) messages.push(newErrors.paymentSource);
      if (newErrors.warehouseId) messages.push(language === 'ar' ? 'المخزن مطلوب' : 'Warehouse is required');
      if (newErrors.poDate) messages.push(newErrors.poDate);
      if (newErrors.expectedDeliveryDate) messages.push(newErrors.expectedDeliveryDate);
      if (newErrors.details) messages.push(language === 'ar' ? 'يجب إضافة صنف واحد على الأقل' : 'At least one item is required');
      const lineErrors = Object.keys(newErrors).filter(k => k.startsWith('item_') || k.startsWith('unit_') || k.startsWith('quantity_'));
      if (lineErrors.length > 0) messages.push(language === 'ar' ? 'يوجد نواقص في بيانات الأصناف' : 'Some item details are incomplete');
      toast.error(messages.join(' | '), { autoClose: 5000 });
    }
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      if (purchaseOrder) {
        const updateDto: UpdatePurchaseOrderDto = {
          purchaseOrderId: purchaseOrder.purchaseOrderId,
          poDate: formData.poDate,
          supplierId: formData.supplierId || undefined,
          custodyId: formData.custodyId || undefined,
          paymentAccountId: formData.paymentAccountId || undefined,
          warehouseId: formData.warehouseId!,
          branchId: formData.branchId || undefined,
          expectedDeliveryDate: formData.expectedDeliveryDate || undefined,
          discountPercent: formData.discountPercent,
          taxPercent: formData.taxPercent,
          shippingCost: formData.shippingCost,
          status: purchaseOrder.status,
          notes: formData.notes || undefined,
          details: detailLines.map((line, index) => ({
            poDetailId: (() => { const p = parseInt(line.id); return !isNaN(p) && p > 0 && p < 2147483647 ? p : 0; })(),
            lineNumber: index + 1,
            itemId: line.itemId!,
            unitId: line.unitId!,
            orderedQuantity: line.orderedQuantity,
            unitPrice: line.unitPrice,
            discountPercent: line.discountPercent,
            taxPercent: line.taxPercent,
            notes: line.notes || undefined,
          })),
        };
        await purchaseOrdersService.update(purchaseOrder.purchaseOrderId, updateDto);
      } else {
        const createDto: CreatePurchaseOrderDto = {
          poNumber: formData.poNumber,
          poDate: formData.poDate,
          supplierId: formData.supplierId || undefined,
          custodyId: formData.custodyId || undefined,
          paymentAccountId: formData.paymentAccountId || undefined,
          warehouseId: formData.warehouseId!,
          branchId: formData.branchId || undefined,
          expectedDeliveryDate: formData.expectedDeliveryDate || undefined,
          discountPercent: formData.discountPercent,
          taxPercent: formData.taxPercent,
          shippingCost: formData.shippingCost,
          notes: formData.notes || undefined,
          details: detailLines.map((line, index) => ({
            lineNumber: index + 1,
            itemId: line.itemId!,
            unitId: line.unitId!,
            orderedQuantity: line.orderedQuantity,
            unitPrice: line.unitPrice,
            discountPercent: line.discountPercent,
            taxPercent: line.taxPercent,
            notes: line.notes || undefined,
          })),
        };
        await purchaseOrdersService.create(createDto);
      }
      toast.success(t.orderSaved);
      onSave();
      handleClose();
    } catch (error: any) {
      console.error('Failed to save:', error);
      toast.error(error.response?.data?.message || t.orderSaveFailed);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      poNumber: '',
      poDate: new Date().toISOString().split('T')[0],
      supplierId: null,
      custodyId: null,
      paymentAccountId: null,
      warehouseId: null,
      branchId: null,
      expectedDeliveryDate: '',
      discountPercent: 0,
      taxPercent: 0,
      shippingCost: 0,
      notes: '',
    });
    setDetailLines([]);
    setSelectedSupplier(null);
    setSelectedCustody(null);
    setSelectedPaymentAccount(null);
    setSelectedWarehouse(null);
    setSelectedBranch(null);
    setSupplierItems([]);
    setErrors({});
    setActiveStep(0);
    setPaymentSource('supplier');
    onClose();
  };

  // Handle payment source change
  const handlePaymentSourceChange = (source: 'supplier' | 'custody' | 'account') => {
    setPaymentSource(source);
    
    // Reset all payment sources
    setFormData({
      ...formData,
      supplierId: null,
      custodyId: null,
      paymentAccountId: null,
    });
    
    setSelectedSupplier(null);
    setSelectedCustody(null);
    setSelectedPaymentAccount(null);
  };

  const totals = calculateTotals();

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ mt: 3 }}>
            {/* Header Card */}
            <Card 
              elevation={6}
              sx={{ 
                mb: 3,
                background: theme.palette.mode === 'dark' 
                  ? 'linear-gradient(145deg, #2d1b4e 0%, #4a2f6b 100%)'
                  : 'linear-gradient(145deg, #f093fb 0%, #f5576c 100%)',
                color: 'white',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: -50,
                  right: -50,
                  width: 200,
                  height: 200,
                  background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
                  borderRadius: '50%',
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                  <ShoppingCartIcon sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {formData.poNumber || '---'}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      {t.poNumber}
                    </Typography>
                  </Box>
                </Stack>

                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, 
                  gap: 2 
                }}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label={t.poDate}
                      value={formData.poDate ? new Date(formData.poDate) : null}
                      onChange={(date) => setFormData({ ...formData, poDate: date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '' })}
                      format="dd/MM/yyyy"
                      views={['year', 'month', 'day']}
                      openTo="day"
                      minDate={minDate}
                      maxDate={maxDate}
                      disabled={dateLocked}
                      slotProps={{ textField: { size: 'small', fullWidth: true, error: !!errors.poDate, helperText: errors.poDate, sx: { '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.15)', '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' }, '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' } }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.9)' }, '& .MuiInputBase-input': { color: 'white' } } } }}
                    />
                  </LocalizationProvider>

                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label={t.delivery}
                      value={formData.expectedDeliveryDate ? new Date(formData.expectedDeliveryDate) : null}
                      onChange={(date) => setFormData({ ...formData, expectedDeliveryDate: date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '' })}
                      format="dd/MM/yyyy"
                      views={['year', 'month', 'day']}
                      openTo="day"
                      disabled={dateLocked}
                      slotProps={{ textField: { size: 'small', fullWidth: true, error: !!errors.expectedDeliveryDate, helperText: errors.expectedDeliveryDate, sx: { '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.15)', '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' } }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.9)' }, '& .MuiInputBase-input': { color: 'white' } } } }}
                    />
                  </LocalizationProvider>
                </Box>
              </CardContent>
            </Card>

            {/* Payment Source Selection */}
            <Card elevation={4} sx={{ mb: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <FormControl component="fieldset" fullWidth error={!!errors.paymentSource}>
                  <FormLabel component="legend" sx={{ mb: 2, fontWeight: 600, fontSize: '1.1rem' }}>
                    {t.paymentSource}
                  </FormLabel>
                  <RadioGroup
                    row
                    value={paymentSource}
                    onChange={(e) => handlePaymentSourceChange(e.target.value as 'supplier' | 'custody' | 'account')}
                  >
                    <FormControlLabel
                      value="supplier"
                      control={<Radio />}
                      label={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <BusinessIcon />
                          <span>{t.supplier}</span>
                        </Stack>
                      }
                    />
                    <FormControlLabel
                      value="custody"
                      control={<Radio />}
                      label={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <PersonIcon />
                          <span>{t.custody}</span>
                        </Stack>
                      }
                    />
                    {!(isBranchManager || isWarehouseKeeper) && (
                    <FormControlLabel
                      value="account"
                      control={<Radio />}
                      label={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <AccountIcon />
                          <span>{t.paymentAccount}</span>
                        </Stack>
                      }
                    />
                    )}
                  </RadioGroup>
                  {errors.paymentSource && (
                    <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                      {errors.paymentSource}
                    </Typography>
                  )}
                </FormControl>

                <Box sx={{ mt: 3 }}>
                  {paymentSource === 'supplier' && (
                    <Autocomplete
                      options={suppliers}
                      getOptionLabel={(option) => `${option.supplierCode} - ${isRTL ? option.supplierNameAr : option.supplierName}`}
                      value={selectedSupplier}
                      onChange={(_, newValue) => {
                        setSelectedSupplier(newValue);
                        setFormData({ ...formData, supplierId: newValue?.supplierId || null });
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t.supplier}
                          required
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start">
                                  <BusinessIcon color="primary" />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  )}

                  {paymentSource === 'custody' && (
                    <Autocomplete
                      options={custodies}
                      getOptionLabel={(option) => `${option.custodyNumber} - ${isRTL ? option.employeeNameAr : option.employeeName}`}
                      value={selectedCustody}
                      onChange={(_, newValue) => {
                        setSelectedCustody(newValue);
                        setFormData({ ...formData, custodyId: newValue?.custodyId || null });
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t.custody}
                          required
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start">
                                  <PersonIcon color="secondary" />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  )}

                  {paymentSource === 'account' && !(isBranchManager || isWarehouseKeeper) && (
                    <Autocomplete
                      options={paymentAccounts}
                      getOptionLabel={(option) => `${option.accountCode} - ${isRTL ? option.accountNameAr : option.accountName}`}
                      value={selectedPaymentAccount}
                      onChange={(_, newValue) => {
                        setSelectedPaymentAccount(newValue);
                        setFormData({ ...formData, paymentAccountId: newValue?.accountId || null });
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t.paymentAccount}
                          required
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start">
                                  <AccountIcon color="info" />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  )}
                </Box>

                {formData.supplierId && supplierItems.length > 0 && (
                  <Alert 
                    severity="success" 
                    icon={<CheckCircleIcon />}
                    sx={{ mt: 3 }}
                  >
                    <strong>{supplierItems.length}</strong> {t.itemsAvailable}
                  </Alert>
                )}
              </CardContent>
            </Card>

            {/* Warehouse & Branch */}
            <Card elevation={4} sx={{ mb: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, 
                  gap: 3 
                }}>
                  <Autocomplete
                    options={warehouses}
                    getOptionLabel={(option) => `${option.warehouseCode} - ${isRTL ? option.warehouseNameAr : option.warehouseName}`}
                    value={selectedWarehouse}
                    onChange={(_, newValue) => {
                      setSelectedWarehouse(newValue);
                      setFormData({ ...formData, warehouseId: newValue?.warehouseId || null });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t.warehouse}
                        required
                        error={!!errors.warehouseId}
                        helperText={errors.warehouseId}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <WarehouseIcon color="secondary" />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />

                  {!isWarehouseKeeper && (
                  <Autocomplete
                    options={branches}
                    getOptionLabel={(option) => `${option.branchCode} - ${isRTL ? option.branchNameAr : option.branchName}`}
                    value={selectedBranch}
                    onChange={(_, newValue) => {
                      setSelectedBranch(newValue);
                      setFormData({ ...formData, branchId: newValue?.branchId || null });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t.branch}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <StoreIcon color="info" />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ mt: 3 }}>
            <Card elevation={4}>
              <CardContent sx={{ p: 0 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <InventoryIcon color="primary" sx={{ fontSize: 32 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {t.items} ({detailLines.length})
                    </Typography>
                  </Stack>
                  <Button 
                    startIcon={<AddIcon />} 
                    onClick={addNewLine} 
                    variant="contained"
                    size="large"
                    sx={{
                      background: 'linear-gradient(45deg, #f093fb 30%, #f5576c 90%)',
                      px: 4,
                    }}
                  >
                    {t.addItem}
                  </Button>
                </Stack>

                <Box ref={gridRef}>
                <TableContainer sx={{ maxHeight: 500 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{
                          bgcolor: theme.palette.mode === 'dark' ? '#1a1a2e' : '#f0f4f8',
                          fontWeight: 700,
                          minWidth: 300,
                        }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <CategoryIcon fontSize="small" color="primary" />
                            <span>{t.item}</span>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ bgcolor: theme.palette.mode === 'dark' ? '#1a1a2e' : '#f0f4f8', fontWeight: 700, minWidth: 180 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <ScaleIcon fontSize="small" color="secondary" />
                            <span>{t.unit}</span>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ bgcolor: theme.palette.mode === 'dark' ? '#1a1a2e' : '#f0f4f8', fontWeight: 700, minWidth: 110 }}>{t.quantity}</TableCell>
                        <TableCell sx={{ bgcolor: theme.palette.mode === 'dark' ? '#1a1a2e' : '#f0f4f8', fontWeight: 700, minWidth: 140 }}>{t.price}</TableCell>
                        <TableCell sx={{ bgcolor: theme.palette.mode === 'dark' ? '#1a1a2e' : '#f0f4f8', fontWeight: 700, minWidth: 100 }}>{t.discount}</TableCell>
                        <TableCell sx={{ bgcolor: theme.palette.mode === 'dark' ? '#1a1a2e' : '#f0f4f8', fontWeight: 700, minWidth: 100 }}>{t.tax}</TableCell>
                        <TableCell sx={{ bgcolor: theme.palette.mode === 'dark' ? '#1a1a2e' : '#f0f4f8', fontWeight: 700, minWidth: 140 }}>{t.total}</TableCell>
                        <TableCell sx={{ bgcolor: theme.palette.mode === 'dark' ? '#1a1a2e' : '#f0f4f8', width: 60 }}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detailLines.map((line, index) => (
                        <TableRow 
                          key={line.id}
                          sx={{ 
                            '&:hover': { 
                              bgcolor: alpha(theme.palette.primary.main, 0.05),
                              transition: 'all 0.2s',
                            } 
                          }}
                        >
                          <TableCell sx={{ py: 2 }}>
                            <Autocomplete
                              size="small"
                              options={items}
                              getOptionLabel={(option) => `${option.itemCode} - ${isRTL ? option.itemNameAr : option.itemName}`}
                              value={line.item}
                              onChange={(_, newValue) => handleItemChange(line.id, newValue)}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  error={!!errors[`item_${index}`]}
                                  placeholder={t.selectItem}
                                />
                              )}
                            />
                            {line.itemPolicy && getPolicyLabel(line.itemPolicy) && (
                              <Chip
                                label={getPolicyLabel(line.itemPolicy)}
                                size="small"
                                color={line.itemPolicy.isPriceControlled ? 'warning' : 'info'}
                                icon={line.itemPolicy.isPriceControlled ? <LockIcon fontSize="small" /> : undefined}
                                sx={{ mt: 0.5 }}
                              />
                            )}
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Autocomplete
                              size="small"
                              options={line.availableUnits}
                              getOptionLabel={(option) => `${option.unitCode} - ${isRTL ? option.unitNameAr : option.unitName}`}
                              value={line.unit}
                              onChange={(_, newValue) => handleUnitChange(line.id, newValue)}
                              disabled={!line.item}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  error={!!errors[`unit_${index}`]}
                                  placeholder={t.selectUnit}
                                />
                              )}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <TextField
                              size="small"
                              value={line.orderedQuantity === 0 ? '' : String(line.orderedQuantity)}
                              onChange={(e) => {
                                const v = e.target.value;
                                if (v === '' || /^\d*\.?\d*$/.test(v)) {
                                  updateLine(line.id, 'orderedQuantity', v === '' || v === '.' ? 0 : parseFloat(v));
                                }
                              }}
                              error={!!errors[`quantity_${index}`]}
                              inputProps={{ inputMode: 'decimal' }}
                              sx={{ minWidth: 90 }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            {(() => {
                              const itemUnit = line.availableUnits.find(u => u.unitId === line.unitId);
                              const defaultPrice = itemUnit?.defaultPurchasePrice || 0;
                              const isPriceHigher = defaultPrice > 0 && line.unitPrice > defaultPrice;

                              return (
                                <TextField
                                  size="small"
                                  value={line.unitPrice === 0 ? '' : String(line.unitPrice)}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    if (v === '' || /^\d*\.?\d*$/.test(v)) {
                                      updateLine(line.id, 'unitPrice', v === '' || v === '.' ? 0 : parseFloat(v));
                                    }
                                  }}
                                  disabled={line.isPriceReadOnly}
                                  inputProps={{ inputMode: 'decimal' }}
                                  helperText={
                                    isPriceHigher
                                      ? (language === 'ar'
                                          ? `⚠️ السعر الأساسي: ${defaultPrice.toFixed(3)} د.ك`
                                          : `⚠️ Base price: ${defaultPrice.toFixed(3)} KD`)
                                      : ''
                                  }
                                  InputProps={{
                                    endAdornment: line.isPriceReadOnly && (
                                      <InputAdornment position="end">
                                        <Tooltip title={t.priceFromSupplier}>
                                          <LockIcon color="warning" fontSize="small" />
                                        </Tooltip>
                                      </InputAdornment>
                                    ),
                                  }}
                                  sx={{
                                    minWidth: 110,
                                    '& .Mui-disabled': {
                                      bgcolor: alpha(theme.palette.warning.main, 0.15),
                                      color: theme.palette.text.primary,
                                      WebkitTextFillColor: theme.palette.text.primary,
                                    },
                                    '& .MuiFormHelperText-root': {
                                      color: 'warning.main',
                                      fontSize: '0.7rem',
                                    }
                                  }}
                                />
                              );
                            })()}
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <TextField
                              size="small"
                              type="number"
                              value={line.discountPercent}
                              onChange={(e) => updateLine(line.id, 'discountPercent', parseFloat(e.target.value) || 0)}
                              inputProps={{ min: 0, step: 0.001 }}
                              InputProps={{
                                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                              }}
                              sx={{ minWidth: 90 }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <TextField
                              size="small"
                              type="number"
                              value={line.taxPercent}
                              onChange={(e) => updateLine(line.id, 'taxPercent', parseFloat(e.target.value) || 0)}
                              inputProps={{ min: 0 }}
                              InputProps={{
                                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                              }}
                              sx={{ minWidth: 90 }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Chip 
                              label={`${calculateLineTotal(line).toFixed(3)} KD`}
                              color="success"
                              size="medium"
                              sx={{ fontWeight: 700, minWidth: 120 }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <IconButton 
                              size="small" 
                              color="error" 
                              onClick={() => removeLine(line.id)}
                              sx={{
                                '&:hover': {
                                  transform: 'scale(1.1)',
                                  transition: 'transform 0.2s',
                                }
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                </Box>
              </CardContent>
            </Card>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2, mb: 3 }}>
              <Card sx={{ 
                background: 'linear-gradient(145deg, #f093fb 0%, #f5576c 100%)',
                color: 'white',
              }}>
                <CardContent>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>{t.subtotal}</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {totals.subTotal.toFixed(3)} <small style={{ fontSize: '0.5em' }}>KD</small>
                  </Typography>
                </CardContent>
              </Card>

              <Card sx={{ 
                background: 'linear-gradient(145deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
              }}>
                <CardContent>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>{t.grandTotal}</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {totals.totalAmount.toFixed(3)} <small style={{ fontSize: '0.5em' }}>KD</small>
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            <Card elevation={4}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
                  <TextField
                    fullWidth
                    label={t.headerDiscount}
                    type="number"
                    size="medium"
                    value={formData.discountPercent}
                    onChange={(e) => setFormData({ ...formData, discountPercent: parseFloat(e.target.value) || 0 })}
                    inputProps={{ min: 0 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">📉</InputAdornment>,
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                  />

                  <TextField
                    fullWidth
                    label={t.headerTax}
                    type="number"
                    size="medium"
                    value={formData.taxPercent}
                    onChange={(e) => setFormData({ ...formData, taxPercent: parseFloat(e.target.value) || 0 })}
                    inputProps={{ min: 0 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">📊</InputAdornment>,
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                  />

                  <TextField
                    fullWidth
                    label={t.shipping}
                    type="number"
                    size="medium"
                    value={formData.shippingCost}
                    onChange={(e) => setFormData({ ...formData, shippingCost: parseFloat(e.target.value) || 0 })}
                    inputProps={{ min: 0 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <ShippingIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: <InputAdornment position="end">KD</InputAdornment>,
                    }}
                  />
                </Box>

                <Divider sx={{ my: 3 }} />

                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography>{t.subtotal}:</Typography>
                    <Typography fontWeight="600">{totals.subTotal.toFixed(3)} KD</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="error">- {t.discount}:</Typography>
                    <Typography color="error" fontWeight="600">{totals.discountAmount.toFixed(3)} KD</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography>+ {t.tax}:</Typography>
                    <Typography fontWeight="600">{totals.taxAmount.toFixed(3)} KD</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography>+ {t.shipping}:</Typography>
                    <Typography fontWeight="600">{formData.shippingCost.toFixed(3)} KD</Typography>
                  </Box>
                  <Divider />
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    p: 2,
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    borderRadius: 2,
                  }}>
                    <Typography variant="h6">{t.grandTotal}:</Typography>
                    <Typography variant="h5" color="success.main" fontWeight="700">
                      {totals.totalAmount.toFixed(3)} KD
                    </Typography>
                  </Box>
                </Stack>

                <TextField
                  fullWidth
                  label={t.notes}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  multiline
                  rows={3}
                  sx={{ mt: 3 }}
                />
              </CardContent>
            </Card>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="xl" 
      fullWidth 
      dir={isRTL ? 'rtl' : 'ltr'}
      PaperProps={{
        sx: {
          minHeight: '95vh',
          maxHeight: '95vh',
          background: theme.palette.mode === 'dark' 
            ? '#0f172a'
            : '#f8fafc',
        }
      }}
    >
      <Box sx={{ 
        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        color: 'white',
        p: 3,
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={2} alignItems="center">
            <ShoppingCartIcon sx={{ fontSize: 50 }} />
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {t.title}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {purchaseOrder ? 'Edit' : 'New'}
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={handleClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Stack>

        <LinearProgress 
          variant="determinate" 
          value={((activeStep + 1) / steps.length) * 100}
          sx={{ 
            mt: 2, 
            height: 6, 
            borderRadius: 3,
            bgcolor: 'rgba(255,255,255,0.2)',
            '& .MuiLinearProgress-bar': {
              bgcolor: '#fbbf24',
            }
          }}
        />
      </Box>

      <Box sx={{ px: 3, pt: 3, bgcolor: theme.palette.mode === 'dark' ? '#1e293b' : 'white' }}>
        <Stepper activeStep={activeStep}>
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel 
                StepIconProps={{
                  sx: {
                    '&.Mui-active': {
                      color: '#f093fb',
                    },
                    '&.Mui-completed': {
                      color: '#10b981',
                    }
                  }
                }}
              >
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: activeStep === index ? 700 : 400,
                    color: activeStep === index ? 'primary.main' : 'text.secondary',
                  }}
                >
                  {label}
                </Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <DialogContent sx={{ px: 3, py: 2, overflow: 'auto' }}>
        <Container maxWidth="xl" disableGutters>
          {renderStepContent()}
        </Container>
      </DialogContent>

      <Box sx={{ 
        p: 3, 
        borderTop: 1, 
        borderColor: 'divider',
        bgcolor: theme.palette.mode === 'dark' ? '#1e293b' : 'white',
      }}>
        <Stack direction="row" spacing={2} justifyContent="space-between">
          <Button
            onClick={() => setActiveStep(prev => Math.max(0, prev - 1))}
            disabled={activeStep === 0}
            size="large"
            variant="outlined"
            sx={{ minWidth: 120 }}
          >
            {isRTL ? 'السابق' : 'Back'}
          </Button>

          <Stack direction="row" spacing={2}>
            <Button 
              onClick={handleClose} 
              size="large" 
              variant="outlined"
              sx={{ minWidth: 120 }}
            >
              {t.cancel}
            </Button>

            {activeStep === steps.length - 1 ? (
              <Button
                onClick={handleSubmit}
                variant="contained"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                size="large"
                sx={{
                  minWidth: 200,
                  background: 'linear-gradient(45deg, #10b981 30%, #059669 90%)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #059669 30%, #047857 90%)',
                  }
                }}
              >
                {t.save}
              </Button>
            ) : (
              <Button
                onClick={() => setActiveStep(prev => Math.min(steps.length - 1, prev + 1))}
                variant="contained"
                size="large"
                sx={{
                  minWidth: 120,
                  background: 'linear-gradient(45deg, #f093fb 30%, #f5576c 90%)',
                }}
              >
                {isRTL ? 'التالي' : 'Next'}
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>
    </Dialog>
  );
};

export default PurchaseOrderDialog;